/**
 * Typed error model for both backends.
 *
 * Clinic API errors: { statusCode, error, code?, details? } (verified shape).
 * Auth errors are Nest-style: { statusCode, error, message } where message
 * may be a string or string[]; account states arrive as messages prefixed
 * "ACCOUNT_*" and OTP throttling as 429 with retryAfterSeconds + Retry-After.
 */

export const ErrorCodes = {
  ValidationFailed: 'VALIDATION_FAILED',
  QueryInvalid: 'QUERY_INVALID',
  QueryFieldNotAllowed: 'QUERY_FIELD_NOT_ALLOWED',
  QueryOpNotAllowed: 'QUERY_OP_NOT_ALLOWED',
} as const;

export class NetworkError extends Error {
  constructor(message = 'network unreachable') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * ZOY-466: the request hit OUR client-side deadline — it never settled, so
 * nobody knows whether the server acted on it.
 *
 * It EXTENDS NetworkError deliberately. Reads then retry under the existing
 * policy with no change to it (`error instanceof NetworkError`), while a
 * write surface can still ask `instanceof TimeoutError` and say the honest
 * thing: not "that failed", but "we could not confirm it".
 */
export class TimeoutError extends NetworkError {
  constructor() {
    super('request exceeded the client deadline');
    this.name = 'TimeoutError';
  }
}

/**
 * Should this failure be reported as an AMBIGUOUS outcome rather than a
 * failure? Only a timeout qualifies: every other error here carries a verdict
 * from the server. Writes must never claim a timed-out action did not happen.
 */
export function isUnconfirmedOutcome(err: unknown): boolean {
  return err instanceof TimeoutError;
}

/**
 * ZOY-610: this tab's clinic context was switched away in ANOTHER tab. No
 * request leaves until the person reloads (or signs out); nothing is retried
 * (it is not a NetworkError) and nothing is reported as the server's verdict.
 */
export class ClinicContextChangedError extends Error {
  constructor() {
    super('clinic context changed in another tab');
    this.name = 'ClinicContextChangedError';
  }
}

export class ApiError extends Error {
  readonly status: number;
  /** Clinic typed code (VALIDATION_FAILED, …) or Auth-provided code where present. */
  readonly code: string | undefined;
  readonly details: unknown;
  /** The raw parsed error body — domain members (e.g. duplicate candidates) live here. */
  readonly body: unknown;
  /** Server-reported wait, from body.retryAfterSeconds or the Retry-After header. */
  readonly retryAfterSeconds: number | undefined;
  /** First human-readable server message, when one exists. */
  readonly serverMessage: string | undefined;

  constructor(args: {
    status: number;
    code?: string;
    details?: unknown;
    retryAfterSeconds?: number;
    serverMessage?: string;
    body?: unknown;
  }) {
    super(args.serverMessage ?? `request failed (${args.status})`);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.details = args.details;
    this.body = args.body;
    this.retryAfterSeconds = args.retryAfterSeconds;
    this.serverMessage = args.serverMessage;
  }

  /**
   * Auth-style code: the typed `code` when present, else the UPPER_SNAKE
   * prefix of a "CODE: human text" message (PASSWORD_CHANGE_REQUIRED: …).
   */
  get authCode(): string | undefined {
    if (typeof this.code === 'string') return this.code;
    const m = this.serverMessage;
    if (typeof m !== 'string') return undefined;
    const head = m.split(':', 1)[0]?.trim();
    return head && /^[A-Z][A-Z0-9_]+$/.test(head) ? head : undefined;
  }

  /** Auth account states: ACCOUNT_NOT_READY, ACCOUNT_DISABLED, … */
  get accountState(): string | undefined {
    const m = this.serverMessage;
    if (typeof m === 'string' && m.startsWith('ACCOUNT_')) {
      return m.split(':', 1)[0]?.trim();
    }
    if (typeof this.code === 'string' && this.code.startsWith('ACCOUNT_')) return this.code;
    return undefined;
  }
}

export function parseErrorBody(
  status: number,
  body: unknown,
  retryAfterHeader?: string | null,
): ApiError {
  let code: string | undefined;
  let details: unknown;
  let retryAfterSeconds: number | undefined;
  let serverMessage: string | undefined;

  if (typeof body === 'object' && body !== null) {
    const o = body as Record<string, unknown>;
    if (typeof o.code === 'string') code = o.code;
    if ('details' in o) details = o.details;
    if (typeof o.retryAfterSeconds === 'number') retryAfterSeconds = o.retryAfterSeconds;
    const msg = o.message ?? o.detail;
    if (typeof msg === 'string') serverMessage = msg;
    else if (Array.isArray(msg) && typeof msg[0] === 'string') serverMessage = msg[0];
  }
  if (retryAfterSeconds === undefined && retryAfterHeader) {
    const n = Number(retryAfterHeader);
    if (Number.isFinite(n) && n >= 0) retryAfterSeconds = n;
  }
  return new ApiError({ status, code, details, retryAfterSeconds, serverMessage, body });
}
